import { JOKERS, levelFromCumulativeXp } from "../config/constants.ts";
import type { Stats } from "../storage/local.ts";
import { snapshot } from "./moves.ts";
import type { Card, GameState } from "./types.ts";

export const JOKER_CARD_ID = 52;

export interface JokerReward {
  reason: "level-up";
  count: number;
  levelsGained: number;
}

export function createJokerCard(): Card {
  return {
    id: JOKER_CARD_ID,
    suit: "S",
    rank: 1,
    faceUp: true,
    isJoker: true,
  };
}

export function ensureJokerState(state: GameState): void {
  const containsJoker = containsJokerCard(state);
  state.joker = {
    usedThisGame: state.joker?.usedThisGame ?? containsJoker,
    pendingSpend: state.joker?.pendingSpend ?? containsJoker,
  };
}

export function isJoker(card: Card | undefined): boolean {
  return card?.isJoker === true;
}

export function canDrawJoker(state: GameState | null, stats: Stats): boolean {
  if (!state || state.finished) return false;
  ensureJokerState(state);
  return availableJokerCards(stats, state) > 0 && !state.joker.usedThisGame;
}

export function availableJokerCards(
  stats: Stats,
  state: GameState | null,
): number {
  const reserved = state?.joker?.pendingSpend ? 1 : 0;
  return Math.max(0, stats.jokerCards - reserved);
}

export function drawJoker(state: GameState): boolean {
  ensureJokerState(state);
  if (state.joker.usedThisGame) return false;
  state.undoStack.push(snapshot(state));
  state.waste.push(createJokerCard());
  state.joker.usedThisGame = true;
  state.joker.pendingSpend = true;
  state.moves += 1;
  return true;
}

export function removeJokersForAutocomplete(state: GameState): boolean {
  if (!containsJokerCard(state)) return false;
  state.undoStack.push(snapshot(state));
  state.tableau = state.tableau.map((pile) => pile.filter((card) => !isJoker(card)));
  state.foundation = state.foundation.map((pile) => pile.filter((card) => !isJoker(card)));
  state.stock = state.stock.filter((card) => !isJoker(card));
  state.waste = state.waste.filter((card) => !isJoker(card));
  return true;
}

export function jokerWasRewound(before: GameState, after: GameState): boolean {
  ensureJokerState(before);
  ensureJokerState(after);
  return before.joker.usedThisGame && !after.joker.usedThisGame;
}

export function finalizePendingJokerSpend(
  stats: Stats,
  state: GameState | null,
): { stats: Stats; spent: boolean } {
  if (!state) return { stats, spent: false };
  ensureJokerState(state);
  if (!state.joker.pendingSpend) return { stats, spent: false };
  state.joker.pendingSpend = false;
  return {
    stats: spendJokerCard(stats),
    spent: stats.jokerCards > 0,
  };
}

export function levelUpJokerReward(
  previousCumulativeXp: number,
  nextCumulativeXp: number,
): JokerReward | null {
  const previousLevel = levelFromCumulativeXp(previousCumulativeXp).level;
  const nextLevel = levelFromCumulativeXp(nextCumulativeXp).level;
  const levelsGained = Math.max(0, nextLevel - previousLevel);
  if (levelsGained === 0) return null;
  return {
    reason: "level-up",
    levelsGained,
    count: levelsGained * JOKERS.LEVEL_UP_REWARD,
  };
}

export function grantJokerReward(stats: Stats, reward: JokerReward | null): Stats {
  if (!reward || reward.count <= 0) return stats;
  return {
    ...stats,
    jokerCards: stats.jokerCards + reward.count,
    jokerCardsEarned: stats.jokerCardsEarned + reward.count,
  };
}

export function spendJokerCard(stats: Stats): Stats {
  if (stats.jokerCards <= 0) return stats;
  return {
    ...stats,
    jokerCards: stats.jokerCards - 1,
    jokerCardsSpent: stats.jokerCardsSpent + 1,
  };
}

export function refundJokerCard(stats: Stats): Stats {
  return {
    ...stats,
    jokerCards: stats.jokerCards + 1,
    jokerCardsSpent: Math.max(0, stats.jokerCardsSpent - 1),
  };
}

function containsJokerCard(state: GameState): boolean {
  return (
    state.tableau.some((pile) => pile.some(isJoker)) ||
    state.foundation.some((pile) => pile.some(isJoker)) ||
    state.stock.some(isJoker) ||
    state.waste.some(isJoker)
  );
}
